import type { CascadeSlotConfig, GridCell, WinResult } from '@core/types/cascade-slot.types';
import { generateGrid } from './SymbolGenerator';
import { evaluateWins } from './WinEvaluator';
import { removeWinningSymbols, applyGravity } from './CascadeProcessor';
import { collectMultipliers, calculateBonusWin } from './MultiplierResolver';

export interface CascadeStep {
  grid: GridCell[][];
  wins: WinResult[];
  cascadeLevel: number;
}

export interface SpinResult {
  initialGrid: GridCell[][];
  cascadeSteps: CascadeStep[];
  tumbleWin: number;
  bonusWin: number;
  totalWin: number;
  scatterCount: number;
  freeSpinsTriggered: boolean;
  freeSpinsAwarded: number;
}

export function startSpin(
  config: CascadeSlotConfig,
  isFreeGame: boolean = false,
): SpinResult {
  const initialGrid = generateGrid(config, undefined, isFreeGame);

  const { cascadeSteps, tumbleWin } = runCascadeLoop(initialGrid, config, isFreeGame);

  // Check BN multipliers on final grid
  const finalGrid = cascadeSteps.length > 0
    ? cascadeSteps[cascadeSteps.length - 1].grid
    : initialGrid;
  const { totalMultiplier } = collectMultipliers(finalGrid);
  const bonusWin = calculateBonusWin(tumbleWin, totalMultiplier);

  // Check scatter
  const scatterCount = countScatters(finalGrid, config.freeSpins.triggerSymbolId);
  const freeSpinsTriggered = isFreeGame
    ? scatterCount >= config.freeSpins.retriggerCount
    : scatterCount >= config.freeSpins.triggerCount;
  const freeSpinsAwarded = freeSpinsTriggered
    ? (isFreeGame ? config.freeSpins.retriggerSpinsAwarded : config.freeSpins.spinsAwarded)
    : 0;

  return {
    initialGrid,
    cascadeSteps,
    tumbleWin,
    bonusWin,
    totalWin: tumbleWin + bonusWin,
    scatterCount,
    freeSpinsTriggered,
    freeSpinsAwarded,
  };
}

export function runCascadeLoop(
  initialGrid: GridCell[][],
  config: CascadeSlotConfig,
  isFreeGame: boolean = false,
): { cascadeSteps: CascadeStep[]; tumbleWin: number } {
  const cascadeSteps: CascadeStep[] = [];
  let tumbleWin = 0;
  let currentGrid = initialGrid;
  let cascadeLevel = 0;
  const MAX_CASCADES = 100;

  while (cascadeLevel < MAX_CASCADES) {
    const wins = evaluateWins(currentGrid, config);

    if (wins.length === 0) break;

    const stepPayout = wins.reduce((sum, w) => sum + w.payout, 0);
    tumbleWin += stepPayout;

    // Record step with deep copy of current grid
    cascadeSteps.push({
      grid: currentGrid.map(row => row.map(cell => ({ ...cell }))),
      wins,
      cascadeLevel,
    });

    // Remove winning symbols
    const gridWithRemovals = removeWinningSymbols(currentGrid, wins);

    // Apply gravity
    const gridAfterGravity = applyGravity(gridWithRemovals, config.cascadeDirection, config.reelHeights);

    // Fill empty spaces
    currentGrid = generateGrid(config, gridAfterGravity, isFreeGame);

    cascadeLevel++;
  }

  return { cascadeSteps, tumbleWin };
}

function countScatters(grid: GridCell[][], scatterId?: string): number {
  let count = 0;
  const ids = scatterId
    ? [scatterId, scatterId + '1'] // e.g. 'F' and 'F1', or 'SC' and 'SC1'
    : ['SC', 'SC1'];
  for (const row of grid) {
    for (const cell of row) {
      if (cell.symbolId && ids.includes(cell.symbolId)) count++;
    }
  }
  return count;
}

function countSymbolInGrid(grid: GridCell[][], symbolId: string): number {
  let count = 0;
  for (const row of grid) {
    for (const cell of row) {
      if (cell.symbolId === symbolId) count++;
    }
  }
  return count;
}

function findScatterTier(
  paytable: { count: number; payout: number }[],
  count: number,
): { count: number; payout: number } | null {
  // Find the highest tier where count >= tier.count
  let best: { count: number; payout: number } | null = null;
  for (const tier of paytable) {
    if (count >= tier.count && (!best || tier.count > best.count)) {
      best = tier;
    }
  }
  return best;
}

export function checkScatter(
  grid: GridCell[][],
  isFreeGame: boolean,
  config: CascadeSlotConfig,
  bet?: number,
): { triggered: boolean; freeSpins: number; scatterWin: number } {
  const scatterId = config.freeSpins.triggerSymbolId || 'SC';
  const scatterCount = countScatters(grid, scatterId);
  const triggerCount = isFreeGame ? config.freeSpins.retriggerCount : config.freeSpins.triggerCount;

  // Calculate scatter pays
  const scCount = countSymbolInGrid(grid, scatterId) + countSymbolInGrid(grid, scatterId + '1');
  const sc1Count = countSymbolInGrid(grid, scatterId + '1');
  const effectiveBet = bet ?? config.betLevels?.[0] ?? 20;

  let scatterWin = 0;

  if (config.scatterPaytable) {
    const tier = findScatterTier(config.scatterPaytable, scCount);
    if (tier) scatterWin += tier.payout * effectiveBet;
  }

  if (config.superScatterPaytable && sc1Count > 0) {
    const tier = findScatterTier(config.superScatterPaytable, sc1Count);
    if (tier) scatterWin += tier.payout * effectiveBet;
  }

  if (scatterCount >= triggerCount) {
    const freeSpins = isFreeGame
      ? config.freeSpins.retriggerSpinsAwarded
      : config.freeSpins.spinsAwarded;
    return { triggered: true, freeSpins, scatterWin };
  }

  return { triggered: false, freeSpins: 0, scatterWin };
}
